// src/monitoring/pages/Reports.jsx - the monthly performance report per park.

import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useMonitoring } from "../MonitoringContext";
import { useMonitoringText } from "../i18n";
import { fetchReport } from "../services/monitoringApi";
import useMonitoringChartTheme from "../components/chartTheme";
import { ReplayBadge, SectionTitle, SimulatedBadge, fmtDate, fmtKw, fmtKwh, fmtKwp, fmtNum, fmtPct, useLocale } from "../components/primitives";

const PALETTE = ["#1976d2", "#26a69a", "#ef6c00", "#7e57c2", "#c62828", "#00838f", "#6d4c41", "#455a64"];

function thisMonth() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Athens", year: "numeric", month: "2-digit" }).format(new Date());
}

export default function Reports() {
  const m = useMonitoring();
  const t = useMonitoringText();
  const locale = useLocale();
  const chartTheme = useMonitoringChartTheme();
  const [month, setMonth] = useState(thisMonth);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!m.projectId || !m.accessToken) return undefined;
    const controller = new AbortController();
    setLoading(true); setError(null);
    fetchReport(m.projectId, month, { accessToken: m.accessToken, signal: controller.signal })
      .then((r) => { if (!controller.signal.aborted) setReport(r); })
      .catch((e) => { if (!controller.signal.aborted) setError(e.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [m.projectId, m.accessToken, month, m.portfolio]);

  const dailyChart = useMemo(() => {
    if (!report) return [];
    const byDay = {};
    report.parks.forEach((p) => p.daily.forEach((d) => { byDay[d.day] = byDay[d.day] || { day: d.day }; byDay[d.day][p.id] = Math.round((d.e_kwh || 0) / 100) / 10; }));
    return Object.values(byDay).sort((a, b) => a.day.localeCompare(b.day)).map((r) => ({ ...r, label: fmtDate(r.day, locale) }));
  }, [report, locale]);

  const prColor = (pr, target) => (pr == null ? undefined : target && pr < target ? "error.main" : pr >= 0.8 ? "success.main" : "warning.main");

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <SectionTitle title={t("reports.title")} hint={t("reports.subtitle")}
          action={<Stack direction="row" spacing={1} alignItems="center">{report?.simulated && <SimulatedBadge />}
            {report?.replay && <ReplayBadge />}
            <TextField type="month" size="small" label={t("reports.month")} value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} /></Stack>} />
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {loading && !report ? <CircularProgress size={22} /> : report && (
          report.parks.length === 0 || report.parks.every((p) => p.days_with_data === 0) ? <Typography color="text.secondary">{t("reports.empty")}</Typography> : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("reports.col.park")}</TableCell>
                    {["capacity", "energy", "expected", "pr", "prTarget", "yield", "availability", "comms", "peak", "alarms", "days"].map((k) => <TableCell key={k} align="right">{t(`reports.col.${k}`)}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.parks.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell><Typography variant="body2" component={RouterLink} to={`/monitoring/parks/${p.id}`} sx={{ color: "primary.main", textDecoration: "none", fontWeight: 600 }}>{p.name}</Typography></TableCell>
                      <TableCell align="right">{fmtKwp(p.plant?.dc_kwp, locale)}</TableCell>
                      <TableCell align="right">{fmtKwh(p.e_kwh, locale)}</TableCell>
                      <TableCell align="right" sx={{ color: "text.secondary" }}>{fmtKwh(p.e_expected_kwh, locale)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: prColor(p.pr, p.pr_target) }}>{fmtPct(p.pr, 1, locale)}</TableCell>
                      <TableCell align="right" sx={{ color: "text.secondary" }}>{fmtPct(p.pr_target, 0, locale)}</TableCell>
                      <TableCell align="right">{p.specific_yield_kwh_kwp == null ? "—" : `${fmtNum(p.specific_yield_kwh_kwp, 1, locale)} kWh/kWp`}</TableCell>
                      <TableCell align="right">{fmtPct(p.availability, 1, locale)}</TableCell>
                      <TableCell align="right">{fmtPct(p.comms_availability, 1, locale)}</TableCell>
                      <TableCell align="right">{fmtKw(p.peak_kw, locale)}</TableCell>
                      <TableCell align="right">{p.alarms?.total ?? 0}{p.alarms?.critical ? ` (${p.alarms.critical} ${t("severity.critical").toLowerCase()})` : ""}</TableCell>
                      <TableCell align="right">{p.days_with_data}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow sx={{ "& td": { fontWeight: 700, color: "text.primary", fontSize: "0.875rem" } }}>
                    <TableCell>{t("reports.total")}</TableCell>
                    <TableCell align="right">{fmtKwp(report.totals.dc_kwp, locale)}</TableCell>
                    <TableCell align="right">{fmtKwh(report.totals.e_kwh, locale)}</TableCell>
                    <TableCell align="right">{fmtKwh(report.totals.e_expected_kwh, locale)}</TableCell>
                    <TableCell align="right">{fmtPct(report.totals.pr, 1, locale)}</TableCell>
                    <TableCell colSpan={5} />
                    <TableCell align="right">{report.totals.alarms}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>
            </Box>
          )
        )}
      </Paper>
      {dailyChart.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <SectionTitle title={t("reports.dailyEnergy")} />
          <Box sx={{ height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={dailyChart} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={chartTheme.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={chartTheme.axisTick} axisLine={chartTheme.axisLine} tickLine={chartTheme.tickLine} interval="preserveStartEnd" />
                <YAxis tick={chartTheme.axisTick} axisLine={chartTheme.axisLine} tickLine={chartTheme.tickLine} unit=" MWh" width={70} />
                <Tooltip {...chartTheme.tooltip} formatter={(v) => `${fmtNum(v, 1, locale)} MWh`} />
                <Legend {...chartTheme.legend} />
                {report.parks.map((p, i) => <Bar key={p.id} dataKey={p.id} name={p.name} stackId="e" fill={PALETTE[i % PALETTE.length]} />)}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}
    </Stack>
  );
}
